// BusConnection — one shared WebSocket to a server URL, ridden by N scripts.
//
// In the shared-bus model (docs/multi-script-client.md §"the connection is a
// shared bus"), the websocket is NOT per-script. Every script connected to
// the same server URL shares one socket:
//
//   - inbound is broadcast: each frame the server sends is delivered to
//     EVERY rider's receive handler;
//   - outbound is merged: any rider may send, and it all flows out the one
//     socket;
//   - the socket is refcounted by its riders — it opens on the first join
//     and closes when the last rider leaves.
//
// This is the transport half of the supervisor's job; the supervisor
// (MunosScriptHost) owns a BusConnection per URL and joins/leaves tenants as
// the forest changes. A tenant interacts with its bus only as a BusRider:
// it hands over outbound bytes and receives inbound ones, but never owns the
// socket, so one mod can never close the pipe out from under a co-rider.
//
// The socket is built through an injectable factory so the broadcast / merge
// / refcount logic is unit-testable headlessly with a fake socket, the same
// way MunosScriptHost is tested through a fake core + fake tenants.

import {
  decodeControlMessage,
  resolveRedirectUrl,
  encodeEnvelope,
  decodeEnvelope,
} from "@multinostalgia/munos";

// Minimal surface of a WebSocket the bus depends on — enough to drive it and
// to stand in a fake during tests. Browser `WebSocket` satisfies it.
export interface BusSocket {
  readonly readyState: number;
  binaryType: string;
  // The reliable/ordered send — every WebSocket-shaped transport has it.
  send(data: Uint8Array): void;
  // Optional unreliable/unordered fast-path. A transport that can deliver a
  // datagram without retransmit/ordering guarantees (WebRTC's unordered,
  // zero-retransmit DataChannel) implements this; the bus routes the
  // unreliable coalescing class here. A transport without it (WebSocket,
  // the loopback, test fakes) omits it and the bus falls back to send() —
  // over a reliable transport the two classes are indistinguishable anyway,
  // which is the intended degradation (docs/coalescing.md).
  sendUnreliable?(data: Uint8Array): void;
  close(): void;
  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: (ev: { data?: unknown }) => void,
  ): void;
}

export type BusSocketFactory = (url: string) => BusSocket;

// A script's view of the bus: it contributes a handoff (sent on connect) and
// receives inbound frames + interest-management leave signals. ScriptTenant
// implements this; the bus never reaches anything else on a rider.
export interface BusRider {
  // Sent to the server on (re)connect. Empty for a passive co-consumer (a
  // radar that only reads). NOTE: with several riders on one socket the
  // server receives several handoffs and cannot yet tell them apart — the
  // demux is the deferred wrinkle in docs/multi-script-client.md; the toy
  // sidesteps it with empty handoffs.
  readonly handoff: Uint8Array;
  dispatchReceive(bytes: Uint8Array): void;
  dispatchPeerLeft(slot: number): void;
}

type StatusFn = (kind: "info" | "error" | "connected", message: string) => void;

// WebSocket.readyState values, named locally so the module needs no DOM
// global at runtime (the fake socket in tests sets these numerically).
const OPEN = 1;
const CLOSING = 2;
const CLOSED = 3;

export class BusConnection {
  private socket: BusSocket | null = null;
  private opened = false;
  private readonly riders = new Set<BusRider>();
  // Outbound coalescing buffers, one per reliability class. Every rider's
  // send() this frame accumulates here and flush() ships them as coalesced
  // envelopes at the end of the frame (docs/coalescing.md). Two buffers so an
  // unreliable frame and a reliable one never share a datagram (a future lossy
  // transport must deliver the classes independently); over a reliable
  // transport both flush the same way.
  private readonly outboundReliable: Uint8Array[] = [];
  private readonly outboundUnreliable: Uint8Array[] = [];

  constructor(
    readonly url: string,
    private readonly makeSocket: BusSocketFactory =
      (u) => new WebSocket(u),
    private readonly onStatus: StatusFn = () => {},
    // A server-pushed redirect on this bus — a GROUP hop. The supervisor
    // reconciles the whole rider set against the destination's served array
    // (evict the diff, keep the overlap, start the new ones), so this is one
    // bus-level callback, NOT a per-rider broadcast. The URL is pre-resolved
    // against this bus's URL. See docs/multi-script-client.md §"Hopping".
    private readonly onRedirect: (url: string, handoff: Uint8Array) => void = () => {},
    // A server-pushed `host_locally` on this bus (docs/node-federation.md): the
    // remote instance tells us to host the current deployment on THIS machine
    // and keep it as our local server's uplink parent. Handed once to the
    // supervisor, which spawns the deployment locally, wires the local server's
    // uplink to `parentUrl` (LocalServer.adoptParent), and hops this bus's
    // riders onto the local bus. `parentUrl` is pre-resolved against this bus.
    private readonly onHostLocally: (parentUrl: string, handoff: Uint8Array) => void = () => {},
  ) {}

  get riderCount(): number { return this.riders.size; }
  isOpen(): boolean { return this.opened; }

  // Add a rider. Opens the socket on the first join; a rider that joins an
  // already-open bus sends its handoff immediately (a rider that joins while
  // the socket is still connecting gets its handoff when `open` fires).
  join(rider: BusRider): void {
    if (this.riders.has(rider)) return;
    this.riders.add(rider);
    if (!this.socket) { this.open(); return; }
    if (this.opened) this.sendHandoff(rider);
  }

  // Remove a rider. Closes the socket once the last rider leaves — the pipe
  // outlives any single mod but not all of them.
  leave(rider: BusRider): void {
    if (!this.riders.delete(rider)) return;
    if (this.riders.size === 0) this.close();
  }

  // Buffer one rider's outbound frame by reliability class for the next
  // flush(). A whole frame's sends are coalesced into as few datagrams as the
  // MTU allows (docs/coalescing.md).
  send(bytes: Uint8Array, reliable: boolean): void {
    (reliable ? this.outboundReliable : this.outboundUnreliable).push(bytes);
  }

  // Ship this frame's buffered outbound frames as coalesced envelopes — one
  // datagram (or a few, past the MTU) per reliability class. The supervisor
  // calls this once, after every rider has taken its frame, so a whole frame's
  // sends leave together. Buffers are cleared even when the socket isn't open,
  // matching the legacy drop-if-not-open send.
  //
  // The reliable class always uses send(); the unreliable class uses the
  // socket's sendUnreliable() when it offers one (WebRTC), else falls back to
  // send(). Over a reliable transport (WebSocket, loopback) both classes flush
  // identically — the split only becomes observable on a transport that can
  // actually deliver the unreliable class differently.
  flush(): void {
    const s = this.socket;
    const open = s !== null && s.readyState === OPEN;
    if (this.outboundReliable.length > 0) {
      if (open) for (const dg of encodeEnvelope(this.outboundReliable)) s.send(dg);
      this.outboundReliable.length = 0;
    }
    if (this.outboundUnreliable.length > 0) {
      if (open) {
        const sendUnreliable = s.sendUnreliable
          ? s.sendUnreliable.bind(s)
          : s.send.bind(s);
        for (const dg of encodeEnvelope(this.outboundUnreliable)) sendUnreliable(dg);
      }
      this.outboundUnreliable.length = 0;
    }
  }

  private open(): void {
    const s = this.makeSocket(this.url);
    s.binaryType = "arraybuffer";
    this.socket = s;
    this.onStatus("info", "");

    s.addEventListener("open", () => {
      // Superseded while connecting (a close()/reopen swapped this.socket):
      // abandon this orphan instead of sending handoffs over it.
      if (this.socket !== s) { s.close(); return; }
      this.opened = true;
      for (const r of this.riders) this.sendHandoff(r);
      this.onStatus("connected", "");
    });

    s.addEventListener("message", (ev) => {
      // Superseded/closed while a frame was already queued on this socket:
      // ignore it, exactly as the open/close/error handlers do. Without this
      // guard a buffered frame from an orphaned socket would be broadcast to
      // the *current* rider set — and a stale control frame (redirect,
      // host_locally) would drive a group hop on the wrong bus generation.
      if (this.socket !== s) return;
      const data = ev.data;
      if (typeof data === "string") { this.handleControl(data); return; }
      const buf = data instanceof ArrayBuffer
        ? new Uint8Array(data)
        : new Uint8Array(0);
      // One datagram carries several frames — split it back into the exact
      // per-frame buffers each rider's channel demux expects.
      const frames = decodeEnvelope(buf);
      // Broadcast: every rider sees every inbound frame. Snapshot the set —
      // a rider's receive handler may spawn (join) or disconnect (leave).
      for (const frame of frames) {
        for (const r of [...this.riders]) r.dispatchReceive(frame);
      }
    });

    s.addEventListener("close", () => {
      if (this.socket !== s) return; // we already moved on
      this.opened = false;
      this.onStatus("error", "disconnected");
    });

    s.addEventListener("error", () => {
      if (this.socket !== s) return;
      this.onStatus("error", `failed to connect to ${this.url}`);
    });
  }

  private close(): void {
    const s = this.socket;
    this.socket = null;
    this.opened = false;
    // Drop any frames buffered for this tick's flush. flush() clears them even
    // while closed, so this is belt-and-suspenders — but it makes the invariant
    // explicit (a closed bus holds no pending outbound) rather than resting on
    // the flush side effect, so a future reopen can't ship a stale pre-close frame.
    this.outboundReliable.length = 0;
    this.outboundUnreliable.length = 0;
    if (s && s.readyState !== CLOSING && s.readyState !== CLOSED) s.close();
  }

  private sendHandoff(rider: BusRider): void {
    const s = this.socket;
    if (s && s.readyState === OPEN) s.send(rider.handoff);
  }

  // Control-plane (text) frames. peer_left is interest-management and
  // broadcasts to every rider (each drops the culled ghost it knows about);
  // redirect is a bus-level GROUP hop handed once to the supervisor, which
  // reconciles every rider against the destination's served array.
  private handleControl(raw: string): void {
    const msg = decodeControlMessage(raw);
    if (msg === null) return;
    if (msg.type === "peer_left") {
      for (const r of [...this.riders]) r.dispatchPeerLeft(msg.slot);
      return;
    }
    if (msg.type === "host_locally") {
      // "Spawn your own local server, keep this instance as parent." Handed to
      // the supervisor (see the onHostLocally note above). parentUrl is
      // absolute (the instance's my_url()); resolve is a no-op for it.
      this.onHostLocally(resolveRedirectUrl(this.url, msg.parentUrl), msg.handoff);
      return;
    }
    // redirect — one group hop for the whole bus. Resolve the destination
    // against this bus's URL (a relative `/path` targets the same origin).
    this.onRedirect(resolveRedirectUrl(this.url, msg.url), msg.handoff);
  }
}
